// T011: Player Model

import type { Player, Card, CardDefinition } from '../../types';
import { getCardScore } from './Card';

/** 手牌上限 */
export const HAND_SIZE_LIMIT = 10;

export function createPlayer(
  id: string,
  name: string,
  faction: string
): Player {
  return {
    id,
    name,
    faction,
    deck: [],
    hand: [],
    graveyard: [],
    forest: [],
  };
}

/**
 * 抽牌，超過手牌上限的牌自動送入墓地
 * @returns 更新後的玩家狀態，以及被丟棄到墓地的卡牌列表
 */
export function drawCards(player: Player, count: number): Player {
  const cardsToDraw = player.deck.slice(0, count);
  const remainingDeck = player.deck.slice(count);

  const drawnCards = cardsToDraw.map((card) => ({ ...card, location: 'hand' as const }));
  const newHand = [...player.hand, ...drawnCards];

  // 手牌溢出處理：超過上限的牌送入墓地
  if (newHand.length > HAND_SIZE_LIMIT) {
    const keptCards = newHand.slice(0, HAND_SIZE_LIMIT);
    const discardedCards = newHand.slice(HAND_SIZE_LIMIT).map((card) => ({
      ...card,
      location: 'graveyard' as const,
    }));

    return {
      ...player,
      deck: remainingDeck,
      hand: keptCards,
      graveyard: [...player.graveyard, ...discardedCards],
    };
  }

  return {
    ...player,
    deck: remainingDeck,
    hand: newHand,
  };
}

/**
 * 抽牌並返回被丟棄的卡牌資訊（用於 UI 顯示）
 */
export function drawCardsWithOverflow(player: Player, count: number): {
  player: Player;
  drawnCards: Card[];
  discardedCards: Card[];
} {
  const cardsToDraw = player.deck.slice(0, count);
  const remainingDeck = player.deck.slice(count);

  const drawnCards = cardsToDraw.map((card) => ({ ...card, location: 'hand' as const }));
  const newHand = [...player.hand, ...drawnCards];

  if (newHand.length > HAND_SIZE_LIMIT) {
    const keptCards = newHand.slice(0, HAND_SIZE_LIMIT);
    const discardedCards = newHand.slice(HAND_SIZE_LIMIT).map((card) => ({
      ...card,
      location: 'graveyard' as const,
    }));

    return {
      player: {
        ...player,
        deck: remainingDeck,
        hand: keptCards,
        graveyard: [...player.graveyard, ...discardedCards],
      },
      drawnCards,
      discardedCards,
    };
  }

  return {
    player: {
      ...player,
      deck: remainingDeck,
      hand: newHand,
    },
    drawnCards,
    discardedCards: [],
  };
}

/**
 * 檢查手牌是否已滿
 */
export function isHandFull(player: Player): boolean {
  return player.hand.length >= HAND_SIZE_LIMIT;
}

export function removeCardFromHand(player: Player, cardId: string): Player {
  return {
    ...player,
    hand: player.hand.filter((card) => card.id !== cardId),
  };
}

export function addCardToGraveyard(player: Player, card: Card): Player {
  return {
    ...player,
    graveyard: [
      ...player.graveyard,
      { ...card, location: 'graveyard' as const },
    ],
  };
}

export function addCardToForest(player: Player, card: Card): Player {
  return {
    ...player,
    forest: [
      ...player.forest,
      { ...card, location: 'forest' as const },
    ],
  };
}

export function isDeckEmpty(player: Player): boolean {
  return player.deck.length === 0;
}

export function getHandSize(player: Player): number {
  return player.hand.length;
}

export function calculateTotalScore(
  battlefieldCards: Card[],
  definitions: Map<string, CardDefinition>
): number {
  let total = 0;

  for (const card of battlefieldCards) {
    total += getCardScore(card, definitions);
  }

  return total;
}
